/**
 * Cognito usernames, subjects, and directory ids — not a person's name.
 * Federated usernames look like `skynetmicrosoft_<subject>`, and the subject
 * is often an Entra object id (`8f2c7a1e-4b0d-4f7e-9b52-1c3e5d6a7b8c`).
 * Keep in step with `looksLikeOpaqueId` in `packages/core/src/issues/users.ts`.
 */
export function looksLikeUserId(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes("@")) return false;
  return (
    /^user_[0-9a-z]+$/i.test(trimmed) ||
    /^skynet[a-z0-9]*_/i.test(trimmed) ||
    /^[a-z0-9]+_[a-z0-9-]{20,}$/i.test(trimmed) ||
    /[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i.test(trimmed) ||
    trimmed.includes("|")
  );
}

/** A display name, or null when the value is still an unresolved user id. */
export function actorName(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed || looksLikeUserId(trimmed)) return null;
  return trimmed;
}

/** The name to show for a person, skipping ids stored as their display name or email. */
export function personLabel(
  user: { displayName: string; email: string } | null | undefined,
  fallback = "Someone",
): string {
  if (!user) return fallback;
  const name = actorName(user.displayName);
  if (name) return name;
  if (user.email.includes("@")) {
    const local = actorName(user.email.split("@")[0]);
    if (local) return local;
  }
  return fallback;
}
